import "./App.css";
import "./components/HomeScreen.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { authAPI, clearStaleSession, getLoginPath } from "./utils/api";
import { resolveActiveDomain, writeActiveDomain } from "./utils/domain";
import { Toaster } from "sonner";
import { loadUserPreferences } from "./services/preferencesApi";
import {
  reportPageLoad,
  resolvePageLoadPage,
} from "./services/pageLoadApi";

const Login = lazy(() => import("./Login"));
const Logout = lazy(() => import("./Logout"));
const GatewayLogin = lazy(() => import("./GatewayLogin"));
const PrivacyPolicy = lazy(() => import("./PrivacyPolicy"));
const HomeScreen = lazy(() => import("./components/HomeScreen"));
const SystemGraphApp = lazy(() => import("./SystemGraphApp"));
const KarmaOneHomeScreen = lazy(() =>
  import("./pages/KarmaOneHomeScreen")
);
const KarmaOneMainPage = lazy(() =>
  import("./components/karma-one/KarmaOneMainPage/KarmaOneMainPage")
);
const KarmaPulseDashboardPage = lazy(() =>
  import("./pages/KarmaPulseDashboardPage")
);
const KarmaOneAdminPage = lazy(() => import("./pages/KarmaOneAdminPage"));
const CompactGraphApp = lazy(() => import("./compact-graph/CompactGraphApp"));
const CompactGraphLandingPage = lazy(() =>
  import("./compact-graph/CompactGraphLandingPage")
);
const CompactGraphDetailedViewPage = lazy(() =>
  import("./compact-graph/CompactGraphDetailedViewPage")
);
const CompactGraphServiceViewPage = lazy(() =>
  import("./compact-graph/CompactGraphServiceViewPage")
);
const DynamicCanvasGraphDiffPage = lazy(() =>
  import("./pages/DynamicCanvasGraphDiffPage")
);

function LazyRoute({ children }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

const DEFAULT_TITLE = "Karma Domain";
const ROUTE_TITLES = {
  "/": "Karma Home",
  "/karma-pulse": "Karma Pulse",
  "/admin": "Admin",
  "/compact-graph": "Compact Graph",
  "/compact-graph/node": "Compact Graph",
  "/compact-graph/service": "Compact Graph",
  // add more routes later if needed
};
function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = ROUTE_TITLES[pathname] ?? DEFAULT_TITLE;
  }, [pathname]);
  return null;
}

function PageLoadReporter() {
  const { pathname } = useLocation();
  const visitRef = useRef({ pathname: null, pageLoadId: null });

  useEffect(() => {
    const page = resolvePageLoadPage(pathname);
    if (!page) return;

    if (visitRef.current.pathname !== pathname) {
      visitRef.current = {
        pathname,
        pageLoadId: crypto.randomUUID(),
      };
    }

    // Fire-and-forget: do not await, no retry
    void reportPageLoad({
      page,
      pageLoadId: visitRef.current.pageLoadId,
    }).catch(() => {});
  }, [pathname]);

  return null;
}

// Protected Route Component
// When landing on a protected route with expired token: try refresh first; if refresh fails, clear session and redirect to /login
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const oauthGatewayEnabled =
    process.env.REACT_APP_OAUTH_GATEWAY_ENABLED === "true";
  const navigate = useNavigate();
  const [refreshChecked, setRefreshChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [domainAllowed, setDomainAllowed] = useState(false);

  const isLocalEnv =
    process.env.NODE_ENV === "development" ||
    window.location.hostname === "localhost";

  const isAuthenticatedSync =
    oauthGatewayEnabled ||
    authAPI.isAuthenticated() ||
    (isLocalEnv && !authAPI.hasStoredToken());

  useEffect(() => {
    if (isAuthenticatedSync) {
      setAllowed(true);
      setRefreshChecked(true);
      return;
    }
    let cancelled = false;
    authAPI.tryRefreshIfExpired().then((ok) => {
      if (cancelled) return;
      setRefreshChecked(true);
      if (ok) {
        setAllowed(true);
      } else {
        clearStaleSession();
        const returnPath = location.pathname + location.search + location.hash;
        navigate(getLoginPath(returnPath), { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticatedSync,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  // When authenticated, ensure current domain (from URL or session) is in allowed_domains; else logout with alert
  useEffect(() => {
    if (!isAuthenticatedSync && !allowed) return;
    let allowedDomains = [];
    try {
      allowedDomains = JSON.parse(
        localStorage.getItem("allowed_domains") || "[]"
      );
    } catch (e) {
      /* ignore */
    }
    // Resolve via the shared rule: URL param wins, else last-used-if-valid, else
    // first allowed domain. This self-heals a stale sessionStorage domain (so we
    // don't log the user out for it) while still catching a URL that explicitly
    // asks for a domain they're not allowed to see.
    const currentDomain = resolveActiveDomain();
    if (
      allowedDomains.length > 0 &&
      currentDomain &&
      !allowedDomains.includes(currentDomain)
    ) {
      // The resolved domain isn't in the allowed list — typically a stale "demo"
      // carried in the URL/storage after switching orgs. Self-heal to the first
      // allowed domain instead of logging the user out: redirect to a valid
      // ?domain= (and sync storage). On reload the URL domain is allowed and this
      // guard passes, so it settles without a loop.
      const fallback = allowedDomains[0];
      writeActiveDomain(fallback);
      const params = new URLSearchParams(window.location.search);
      params.set("domain", fallback);
      window.location.replace(
        `${window.location.pathname}?${params.toString()}`
      );
      return;
    }
    setDomainAllowed(true);
  }, [isAuthenticatedSync, allowed, location.search, location.pathname]);

  // Load user preferences once when authenticated (user-level, not org-scoped).
  useEffect(() => {
    if (!domainAllowed) return;
    void loadUserPreferences();
  }, [domainAllowed]);

  if (isAuthenticatedSync || allowed) {
    if (!domainAllowed) {
      return null;
    }
    return children;
  }
  if (!refreshChecked) {
    return null;
  }
  return null;
};

// Route-level component: show HomeScreen (centered search) or SystemGraphApp (full graph)
const GraphRoute = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isPinned = params.get("pinned") === "true" && !!params.get("itemId");
  const hasGraphParams =
    isPinned || params.has("timeline") || params.has("version");

  if (hasGraphParams) {
    return (
      <LazyRoute>
        <SystemGraphApp />
      </LazyRoute>
    );
  }
  return (
    <LazyRoute>
      <HomeScreen />
    </LazyRoute>
  );
};

function App() {
  useEffect(() => {
    sessionStorage.removeItem("interfaceId");
  }, []);

  return (
    <div className="App">
      <Router>
        <DocumentTitle />
        <PageLoadReporter />
        <Routes>
          <Route
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <KarmaOneHomeScreen />
                </LazyRoute>
              </ProtectedRoute>
            }
          >
            <Route
              path="/"
              element={
                <LazyRoute>
                  <KarmaOneMainPage />
                </LazyRoute>
              }
            />
            <Route path="/graph" element={<GraphRoute />} />
            <Route
              path="/karma-pulse"
              element={
                <LazyRoute>
                  <KarmaPulseDashboardPage />
                </LazyRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <LazyRoute>
                  <KarmaOneAdminPage />
                </LazyRoute>
              }
            />
            <Route
              path="/canvas/dynamic/graph"
              element={
                <LazyRoute>
                  <DynamicCanvasGraphDiffPage />
                </LazyRoute>
              }
            />
          </Route>
          <Route
            path="/login"
            element={
              <LazyRoute>
                {process.env.REACT_APP_OAUTH_GATEWAY_ENABLED === "true" ? (
                  <GatewayLogin />
                ) : (
                  <Login />
                )}
              </LazyRoute>
            }
          />
          <Route
            path="/logout"
            element={
              <LazyRoute>
                <Logout />
              </LazyRoute>
            }
          />
          <Route
            path="/compact-graph"
            element={
              <LazyRoute>
                <CompactGraphApp />
              </LazyRoute>
            }
          >
            <Route
              index
              element={
                <LazyRoute>
                  <CompactGraphLandingPage />
                </LazyRoute>
              }
            />
            <Route
              path="node"
              element={
                <LazyRoute>
                  <CompactGraphDetailedViewPage />
                </LazyRoute>
              }
            />
            <Route
              path="service"
              element={
                <LazyRoute>
                  <CompactGraphServiceViewPage />
                </LazyRoute>
              }
            />
          </Route>
          <Route
            path="/privacy-policy"
            element={
              <LazyRoute>
                <PrivacyPolicy />
              </LazyRoute>
            }
          />
        </Routes>
      </Router>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          classNames: {
            toast: "karma-toast",
            warning: "karma-toast-warning",
          },
        }}
      />
    </div>
  );
}

export default App;
